/** Shared node-placement math for every "create a node beside an
 * existing one" interaction (VISTOERA_CONTENT_CANVAS_MEDIA_NODE_HOVER_ACTIONS_CONNECTION_PORTS_POPOVER_AND_PLACEMENT_P1
 * §15–§20) — Contextual Creation Rail ("生成图片"/"生成视频"/"生成文本"),
 * "不覆盖，另外生成", and plain node "复制" all funnel through
 * `placeNodeBesideSource` so none of them re-invents its own absolute
 * offset math, and none of them can ever place a new node overlapping
 * its own source node (spec §16 "绝对规则" — `NEW_NODE_BOUNDS` must
 * never intersect `SOURCE_NODE_BOUNDS`).
 *
 * Deliberately NOT a general canvas auto-layout/packing engine (spec
 * §17/§20 "不要写复杂 packing engine") — temporary overlap with OTHER,
 * unrelated nodes is explicitly acceptable; the user can drag the new
 * node away afterwards. */

import type { Node, ReactFlowInstance } from '@xyflow/react'

/** Same 260px fallback every media card already renders at before its
 * first real measurement (`ImageAssetNode`/`GenerateImageNode`'s own
 * `computeMediaBoxSize` empty-state, `GenerateVideoNode`'s fixed
 * width) — used only when a node's real width truly can't be read yet. */
export const DEFAULT_NODE_WIDTH_FALLBACK = 260

/** Spec §15/§18 "建议 80–140px" — how far clear of the source node's
 * right edge a newly created node lands. */
export const NODE_PLACEMENT_HORIZONTAL_GAP = 96

/** Spec §18 "轻量 offset" (e.g. `y += 40`) — a small vertical nudge so a
 * chain of same-source creations (image → generate image → regenerate
 * separately → …) doesn't stack every sibling on the exact same row. */
export const NODE_PLACEMENT_VERTICAL_NUDGE = 32

/** Best-effort real width of a live React Flow node — measured
 * (`node.measured.width`, v12's post-render size) first, then the
 * explicit `node.width` some node types set inline (Scene containers,
 * generator cards with `hasResult` sizing), then the shared fallback.
 * Never `0`/`NaN` — every caller can add this straight into an X
 * offset with no extra guarding. */
export function getNodeWidth(node: Node): number {
  const measured = node.measured?.width as number | undefined
  const explicit = node.width as number | undefined
  return measured || explicit || DEFAULT_NODE_WIDTH_FALLBACK
}

export function getNodeHeight(node: Node): number {
  const measured = node.measured?.height as number | undefined
  const explicit = node.height as number | undefined
  return measured || explicit || DEFAULT_NODE_WIDTH_FALLBACK
}

export interface PlaceBesideSourceOptions {
  /** Horizontal clearance from the source's right edge. Defaults to
   * `NODE_PLACEMENT_HORIZONTAL_GAP`. */
  gap?: number
  /** Extra vertical offset applied on top of the source's own Y
   * (spec §18's "轻量 offset") — 0 by default (new node lands level
   * with its source unless a caller has a specific reason to nudge). */
  verticalNudge?: number
}

/** The one placement rule every "create beside source" interaction
 * shares: `x = source.x + source.width + gap`, `y = source.y +
 * verticalNudge`. Because the new node's X is always strictly greater
 * than the source's right edge, the two rectangles can never overlap
 * on the X axis — which alone guarantees zero intersection area
 * regardless of either node's height (spec §16/§41
 * "SOURCE_NEW_INTERSECTION_AREA = 0"), with no extra collision check
 * needed against the source specifically. */
export function placeNodeBesideSource(source: Node, options: PlaceBesideSourceOptions = {}): { x: number; y: number } {
  const gap = options.gap ?? NODE_PLACEMENT_HORIZONTAL_GAP
  const verticalNudge = options.verticalNudge ?? 0
  const width = getNodeWidth(source)
  return {
    x: source.position.x + width + gap,
    y: source.position.y + verticalNudge,
  }
}

/** VistoeraTASK-00509 — extra results of ONE generation run laid out as a
 * single row to the RIGHT of the source (result 2, 3, … left-to-right, in
 * the run's own stable output order). `x` clears the source's real width
 * and each sibling advances by a collapsed result card's width + gap, so
 * siblings never overlap the source or each other. Reuses `getNodeWidth`;
 * deliberately not a packing engine. */
export const RESULT_SIBLING_CARD_WIDTH_PX = DEFAULT_NODE_WIDTH_FALLBACK
export const RESULT_SIBLING_GAP_PX = 40

export function placeResultSiblingsInRow(
  source: Node,
  count: number,
  options: { gap?: number; siblingWidth?: number } = {},
): Array<{ x: number; y: number }> {
  const gap = options.gap ?? RESULT_SIBLING_GAP_PX
  const siblingWidth = options.siblingWidth ?? RESULT_SIBLING_CARD_WIDTH_PX
  const startX = source.position.x + getNodeWidth(source) + gap
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({
    x: startX + i * (siblingWidth + gap),
    y: source.position.y,
  }))
}

/** VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_IDEATION_BRANCHING_
 * NODE_V1 §31/§34 — "生成结果必须在 Canvas上生成独立节点...自动放在父节点
 * 右侧或下游位置...保持间距、不重叠". A single `placeNodeBesideSource`
 * call places every candidate at the SAME (x, y), so a batch of N
 * candidates from one Ideation/Expand run fans out vertically instead
 * (still `x = source.x + width + gap` for every one of them — so, same
 * as `placeNodeBesideSource`, this can never overlap the source on the
 * X axis — spaced by `rowHeight + verticalGap` down the Y axis so they
 * don't overlap EACH OTHER either). */
export function placeSiblingNodesBesideSource(
  source: Node,
  count: number,
  options: PlaceBesideSourceOptions & { rowHeight?: number; verticalGap?: number } = {},
): Array<{ x: number; y: number }> {
  const gap = options.gap ?? NODE_PLACEMENT_HORIZONTAL_GAP
  const verticalNudge = options.verticalNudge ?? 0
  const width = getNodeWidth(source)
  const rowHeight = options.rowHeight ?? 220
  const verticalGap = options.verticalGap ?? 24
  const x = source.position.x + width + gap
  // Center the fan-out vertically on the source node's own vertical
  // midpoint rather than starting flush with its top edge, so a single
  // candidate lands roughly level with its source and a batch of 3
  // straddles it evenly (matches the reference mock's layout);
  // `verticalNudge` (e.g. successive "再生成一批" batches, spec §41)
  // shifts the WHOLE fan-out down from there.
  const sourceHeight = getNodeHeight(source)
  const totalHeight = count * rowHeight + (count - 1) * verticalGap
  const startY = source.position.y + sourceHeight / 2 - totalHeight / 2 + verticalNudge
  return Array.from({ length: count }, (_, i) => ({
    x,
    y: startY + i * (rowHeight + verticalGap),
  }))
}

/**
 * Expanded Generate Image card width estimate for LEFT-OF-TARGET placement
 * (P0_V3). Prefer `GENERATION_CARD_EXPANDED_WIDTH_PX` (currently 480) when the
 * card is immediately expanded; keep a safe upper bound so a wider expanded
 * Generate Image never overlaps Generate Video.
 */
export const GENERATE_IMAGE_EXPANDED_WIDTH_ESTIMATE = 520

/** Place a new node to the LEFT of a target (e.g. Voice Template ← Generate Audio,
 * Keyframe Generate Image ← Generate Video). `newNodeWidth` must be the
 * EXPANDED width when the new node will immediately open expanded. */
export function placeNodeLeftOfTarget(
  target: Node,
  newNodeWidth: number,
  options: PlaceBesideSourceOptions = {},
): { x: number; y: number } {
  const gap = options.gap ?? NODE_PLACEMENT_HORIZONTAL_GAP
  const verticalNudge = options.verticalNudge ?? 0
  const width = newNodeWidth || DEFAULT_NODE_WIDTH_FALLBACK
  return {
    x: target.position.x - width - gap,
    y: target.position.y + verticalNudge,
  }
}

/** VistoeraTASK-00574 — fan out N new image_asset nodes to the LEFT of a
 * Generate Image target so they do not overlap each other or the target.
 * Mirrors ``placeSiblingNodesBesideSource`` but on the input (left) side. */
export function placeSiblingNodesLeftOfTarget(
  target: Node,
  count: number,
  newNodeWidth: number,
  options: PlaceBesideSourceOptions & { rowHeight?: number; verticalGap?: number } = {},
): Array<{ x: number; y: number }> {
  if (count <= 0) return []
  const gap = options.gap ?? NODE_PLACEMENT_HORIZONTAL_GAP
  const verticalNudge = options.verticalNudge ?? 0
  const width = newNodeWidth || DEFAULT_NODE_WIDTH_FALLBACK
  const rowHeight = options.rowHeight ?? 220
  const verticalGap = options.verticalGap ?? 24
  const x = target.position.x - width - gap
  const sourceHeight = getNodeHeight(target)
  const totalHeight = count * rowHeight + (count - 1) * verticalGap
  const startY = target.position.y + sourceHeight / 2 - totalHeight / 2 + verticalNudge
  return Array.from({ length: count }, (_, i) => ({
    x,
    y: startY + i * (rowHeight + verticalGap),
  }))
}

/** VISTOERA_VIDEO_MERGE_…_VIEWPORT_STABILITY_P0_V1 — select/expand a
 * node WITHOUT changing the user's current Canvas zoom.
 *
 * - If the node is already fully visible in the React Flow pane: no-op
 *   (viewport completely untouched).
 * - Otherwise: `setCenter` at the node's flow center with the CURRENT
 *   zoom (pan only). Never `fitView` — fitView recomputes zoom from
 *   bounding boxes and is the root cause of Shot-click zoom-out. */
export function focusNodePreserveZoom(
  rf: Pick<ReactFlowInstance, 'getNode' | 'getViewport' | 'setCenter'>,
  nodeId: string,
  options: { duration?: number; paddingPx?: number } = {},
): void {
  const duration = options.duration ?? 400
  const paddingPx = options.paddingPx ?? 12
  const node = rf.getNode(nodeId)
  if (!node) return

  const el =
    document.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(nodeId)}"]`) ||
    document.querySelector<HTMLElement>(`[data-testid="rf__node-${CSS.escape(nodeId)}"]`)
  const pane = document.querySelector<HTMLElement>('.react-flow__renderer')
  if (el && pane) {
    const rect = el.getBoundingClientRect()
    const paneRect = pane.getBoundingClientRect()
    const fullyVisible =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left >= paneRect.left + paddingPx &&
      rect.right <= paneRect.right - paddingPx &&
      rect.top >= paneRect.top + paddingPx &&
      rect.bottom <= paneRect.bottom - paddingPx
    if (fullyVisible) return
  }

  const width = getNodeWidth(node)
  const height = getNodeHeight(node)
  const cx = node.position.x + width / 2
  const cy = node.position.y + height / 2
  const { zoom } = rf.getViewport()
  rf.setCenter(cx, cy, { zoom, duration })
}

